
import { Syringe, Pill } from 'lucide-react';
import { Medication } from './index';

export const mensHealthMedications: Medication[] = [
  {
    id: '14',
    name: 'Testosterone Cypionate',
    description: 'An injectable form of testosterone replacement therapy (TRT) used to treat hypogonadism in men, while supporting muscle growth, energy, and libido.',
    research: 'Testosterone Cypionate - Testosterone esters like cypionate correct symptoms in 90% of hypogonadal men by week 4 of treatment, with peak levels 2-4 days post-injection. Improved mood, libido, and body composition follow.',
    url: 'https://www.mayoclinicproceedings.org/article/S0025-6196(11)60434-3/fulltext',
    category: 'mens-health',
    Icon: Syringe,
    video: 'H64KAkM0wF4',
    availablePreparations: 'Injection (e.g., 200 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '100-200 mg IM every 2 weeks or 50-100 mg weekly (individualized).',
      administrationProtocols: 'Deep intramuscular injection in gluteal muscle or lateral thigh.',
      contraindications: 'Male breast cancer, prostate cancer, polycythemia, severe sleep apnea.',
      monitoringRequirements: 'Monitor hematocrit, PSA, liver enzymes; assess mood, libido, and secondary sex characteristics.'
    },
    adverseReactions: {
      common: 'Injection site pain, mood swings, acne.',
      lessCommon: 'Gynecomastia, water retention, elevated hematocrit.',
      rare: 'Venous thromboembolism, hepatic dysfunction.'
    }
  },
  {
    id: '15',
    name: 'Testosterone Enanthate',
    description: 'A slower-releasing injectable testosterone ester that provides a steady release of testosterone for the treatment of hypogonadism.',
    research: 'Testosterone Enanthate - Studies comparing testosterone cypionate and enanthate show identical efficacy and pharmacokinetics, with both providing therapeutic levels for 10-14 days in most men.',
    url: 'https://www.sciencedirect.com/science/article/abs/pii/S0015028216467762',
    category: 'mens-health',
    Icon: Syringe,
    video: 'H64KAkM0wF4',
    availablePreparations: 'Injection (e.g., 200 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '100-200 mg IM every 2 weeks or 50-100 mg weekly (individualized).',
      administrationProtocols: 'Deep intramuscular injection in gluteal muscle or lateral thigh.',
      contraindications: 'Male breast cancer, prostate cancer, polycythemia, severe sleep apnea.',
      monitoringRequirements: 'Monitor hematocrit, PSA, liver enzymes; assess mood, libido, and secondary sex characteristics.'
    },
    adverseReactions: {
      common: 'Injection site pain, mood swings, acne.',
      lessCommon: 'Gynecomastia, water retention, elevated hematocrit.',
      rare: 'Venous thromboembolism, hepatic dysfunction.'
    }
  },
  {
    id: '16',
    name: 'Testosterone Cream',
    description: 'A topical testosterone formula that delivers hormone replacement through the skin, avoiding first-pass liver metabolism.',
    research: 'Topical Testosterone - Transdermal testosterone preparations provide more stable hormone levels than injections, with 2-5 fold less DHT conversion, potentially reducing androgenic side effects.',
    url: 'https://www.mayoclinicproceedings.org/article/S0025-6196(16)00132-2/fulltext',
    category: 'mens-health',
    Icon: Pill,
    video: 'H64KAkM0wF4',
    availablePreparations: 'Topical Cream (e.g., 200 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '50-100 mg applied once daily (compounded).',
      administrationProtocols: 'Apply to clean, dry skin of shoulders, upper arms, or abdomen; wash hands thoroughly after application.',
      contraindications: 'Male breast cancer, prostate cancer, skin irritation at application site.',
      monitoringRequirements: 'Monitor T levels, hematocrit, PSA, and skin reactions; assess transfer risk to women/children.'
    },
    adverseReactions: {
      common: 'Skin irritation, acne, altered libido.',
      lessCommon: 'Headache, fluid retention, gynecomastia.',
      rare: 'Secondary exposure to women/children, allergic reactions.'
    }
  },
  {
    id: '17',
    name: 'Tadalafil',
    description: 'A PDE5 inhibitor that enhances erectile function and is also used for benign prostatic hyperplasia. It has a longer duration of action than sildenafil.',
    research: 'Tadalafil for ED - Tadalafil maintains erectile efficacy for 24-36 hours at 10-20 mg doses, with a 70-85% success rate compared to placebo, and benefits prostate symptoms.',
    url: 'https://www.sciencedirect.com/science/article/abs/pii/S0090429513012843',
    category: 'mens-health',
    Icon: Pill,
    video: 'uEZpg0n7jcY',
    availablePreparations: 'Oral Tablets (e.g., 5 mg, 20 mg)',
    clinicalDetails: {
      dosingGuidelines: 'ED: 10-20 mg PRN or 2.5-5 mg daily. BPH: 5 mg daily.',
      administrationProtocols: 'Take with or without food; for PRN use, take 1-2 hours before activity.',
      contraindications: 'Nitrate use, unstable cardiovascular disease, recent stroke/MI, severe liver/kidney impairment.',
      monitoringRequirements: 'Monitor BP, vision changes; assess sexual function, BPH symptoms.'
    },
    adverseReactions: {
      common: 'Headache, dyspepsia, back pain, myalgia.',
      lessCommon: 'Flushing, nasal congestion, dizziness.',
      rare: 'Priapism, sudden hearing/vision loss, hypotension.'
    }
  },
  {
    id: '18',
    name: 'Sildenafil',
    description: 'The first approved PDE5 inhibitor for erectile dysfunction. It has a shorter duration of action compared to tadalafil but remains highly effective.',
    research: 'Sildenafil Efficacy - Multiple clinical trials demonstrate 75-85% efficacy for sildenafil 50-100 mg in ED treatment, regardless of etiology, with effects lasting 3-5 hours.',
    url: 'https://www.nejm.org/doi/full/10.1056/NEJM199805143382001',
    category: 'mens-health',
    Icon: Pill,
    video: 'uEZpg0n7jcY',
    availablePreparations: 'Oral Tablets (e.g., 20 mg, 100 mg)',
    clinicalDetails: {
      dosingGuidelines: '25-100 mg PO 30-60 minutes before activity (individualized).',
      administrationProtocols: 'Take without food for optimal absorption; fatty meals may delay onset.',
      contraindications: 'Nitrate use, unstable cardiovascular disease, recent stroke/MI, severe hepatic impairment.',
      monitoringRequirements: 'Monitor BP, vision changes; assess sexual function, drug interactions.'
    },
    adverseReactions: {
      common: 'Headache, flushing, dyspepsia, nasal congestion.',
      lessCommon: 'Dizziness, visual disturbances, muscle pain.',
      rare: 'Priapism, sudden hearing/vision loss, hypotension.'
    }
  }
];
